import { useState, useRef, useEffect } from "react";
import Markdown from "./Markdown";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { redact as redactText } from "../lib/redact";
import { Code, Eye, PencilSimple, ListBullets, Quotes, LinkSimple, TextB, TextItalic, Terminal, Image as ImgIcon, Eraser, Spinner } from "@phosphor-icons/react";

const TOOLBAR = [
    { icon: TextB, label: "bold", wrap: "**", title: "Bold" },
    { icon: TextItalic, label: "italic", wrap: "_", title: "Italic" },
    { icon: Code, label: "inline-code", wrap: "`", title: "Inline code" },
    { icon: Terminal, label: "code-block", block: "```bash\n", blockEnd: "\n```", title: "Code block (bash)" },
    { icon: ListBullets, label: "list", linePrefix: "- ", title: "List" },
    { icon: Quotes, label: "quote", linePrefix: "> ", title: "Quote" },
    { icon: LinkSimple, label: "link", template: "[text](https://)", title: "Link" },
];

const MENTION_TRIGGER_RX = /(?:^|[^a-zA-Z0-9_])@([a-zA-Z0-9_]{1,24})$/;

export default function MarkdownEditor({
    value,
    onChange,
    placeholder = "Markdown supported. Use ``` for code blocks. Type @ to mention.",
    minHeight = "h-40",
    testid = "markdown-editor",
    required = false,
}) {
    const [tab, setTab] = useState("write");
    const [mention, setMention] = useState(null); // {query, start, end, results, index}
    const [uploading, setUploading] = useState(false);
    const ref = useRef(null);
    const fileRef = useRef(null);
    const debounceRef = useRef(null);

    const insertAtCursor = (snippet) => {
        const ta = ref.current;
        if (!ta) { onChange((value || "") + snippet); return; }
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const text = value || "";
        onChange(text.slice(0, start) + snippet + text.slice(end));
        requestAnimationFrame(() => {
            ta.focus();
            const pos = start + snippet.length;
            ta.setSelectionRange(pos, pos);
        });
    };

    const uploadImage = async (file) => {
        if (!file?.type?.startsWith("image/")) return;
        if (file.size > 4 * 1024 * 1024) { toast.error("Max 4MB"); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload/markdown-image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const name = file.name.replace(/\.[^.]+$/, "").slice(0, 40) || "image";
            insertAtCursor(`\n![${name}](${data.url})\n`);
            toast.success("Image uploaded");
        } catch (e) { toast.error(formatErr(e)); }
        setUploading(false);
    };

    const onPaste = async (e) => {
        const items = Array.from(e.clipboardData?.items || []);
        const file = items.find((i) => i.kind === "file" && i.type.startsWith("image/"))?.getAsFile();
        if (file) { e.preventDefault(); await uploadImage(file); }
    };

    const onDrop = async (e) => {
        if (!e.dataTransfer?.files?.length) return;
        e.preventDefault();
        await uploadImage(e.dataTransfer.files[0]);
    };

    const onRedact = () => {
        const { text, count } = redactText(value || "");
        if (count === 0) { toast.info("No IPs / hosts / tokens detected"); return; }
        onChange(text);
        toast.success(`Redacted ${count} sensitive token${count > 1 ? "s" : ""}`);
    };

    const insert = (tool) => {
        const ta = ref.current;
        if (!ta) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const text = value || "";
        const selected = text.slice(start, end);
        let nextText, cursor;

        if (tool.wrap) {
            nextText = text.slice(0, start) + tool.wrap + (selected || tool.label) + tool.wrap + text.slice(end);
            cursor = start + tool.wrap.length + (selected || tool.label).length + tool.wrap.length;
        } else if (tool.block) {
            const body = selected || "your code here";
            nextText = text.slice(0, start) + tool.block + body + tool.blockEnd + text.slice(end);
            cursor = start + tool.block.length + body.length + tool.blockEnd.length;
        } else if (tool.linePrefix) {
            const lines = (selected || "item").split("\n");
            const block = lines.map((l) => tool.linePrefix + l).join("\n");
            nextText = text.slice(0, start) + block + text.slice(end);
            cursor = start + block.length;
        } else if (tool.template) {
            nextText = text.slice(0, start) + tool.template + text.slice(end);
            cursor = start + tool.template.length;
        } else {
            return;
        }
        onChange(nextText);
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(cursor, cursor);
        });
    };

    // Detect @mention trigger on every value change while in "write"
    const detectMention = (text, caret) => {
        if (caret == null) return null;
        const upToCaret = text.slice(0, caret);
        const m = MENTION_TRIGGER_RX.exec(upToCaret);
        if (!m) return null;
        const query = m[1];
        const start = caret - query.length - 1; // index of '@'
        return { query, start, end: caret };
    };

    const onTextChange = (e) => {
        const text = e.target.value;
        onChange(text);
        const caret = e.target.selectionStart;
        const m = detectMention(text, caret);
        if (!m) {
            setMention(null);
            return;
        }
        // Debounced lookup
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const { data } = await api.get(`/users/search?q=${encodeURIComponent(m.query)}&limit=6`);
                setMention({ ...m, results: data, index: 0 });
            } catch {
                setMention({ ...m, results: [], index: 0 });
            }
        }, 120);
    };

    const acceptMention = (u) => {
        if (!mention) return;
        const text = value || "";
        const before = text.slice(0, mention.start);
        const after = text.slice(mention.end);
        const insertText = `@${u.username} `;
        const next = before + insertText + after;
        const cursor = before.length + insertText.length;
        onChange(next);
        setMention(null);
        requestAnimationFrame(() => {
            const ta = ref.current;
            if (ta) { ta.focus(); ta.setSelectionRange(cursor, cursor); }
        });
    };

    const onKeyDown = (e) => {
        if (!mention || !mention.results?.length) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setMention((m) => ({ ...m, index: (m.index + 1) % m.results.length }));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setMention((m) => ({ ...m, index: (m.index - 1 + m.results.length) % m.results.length }));
        } else if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            acceptMention(mention.results[mention.index]);
        } else if (e.key === "Escape") {
            setMention(null);
        }
    };

    // Close mention popover if user clicks anywhere outside the textarea
    useEffect(() => {
        const handler = (ev) => {
            if (ref.current && !ref.current.contains(ev.target)) setMention(null);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="border border-white/10 rounded-sm overflow-hidden relative" data-testid={testid}>
            <div className="flex items-center justify-between bg-slate-900/40 border-b border-white/10 px-2 py-1.5">
                <div className="flex items-center gap-0.5" data-testid="md-toolbar">
                    {TOOLBAR.map((t) => (
                        <button
                            key={t.label}
                            type="button"
                            onClick={() => { setTab("write"); insert(t); }}
                            title={t.title}
                            className="p-1.5 text-slate-400 hover:text-cyan-400 hover:bg-slate-800 rounded-sm transition-colors"
                            data-testid={`md-tool-${t.label}`}
                        >
                            <t.icon size={14} weight="bold" />
                        </button>
                    ))}
                    <span className="w-px h-4 bg-slate-800 mx-1" />
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        title="Upload image (or paste/drop)"
                        className="p-1.5 text-slate-400 hover:text-cyan-400 hover:bg-slate-800 rounded-sm transition-colors disabled:opacity-50"
                        data-testid="md-tool-image"
                        disabled={uploading}
                    >
                        {uploading ? <Spinner size={14} className="animate-spin" /> : <ImgIcon size={14} weight="bold" />}
                    </button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.target.value = ""; }}
                    />
                    <button
                        type="button"
                        onClick={onRedact}
                        title="Redact IPs / hosts / tokens"
                        className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-slate-800 rounded-sm transition-colors"
                        data-testid="md-tool-redact"
                    >
                        <Eraser size={14} weight="bold" />
                    </button>
                </div>
                <div className="flex items-center gap-0.5">
                    <button
                        type="button"
                        onClick={() => setTab("write")}
                        className={`flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest rounded-sm transition-colors ${tab === "write" ? "bg-slate-800 text-cyan-400" : "text-slate-500 hover:text-cyan-400"}`}
                        data-testid="md-tab-write"
                    >
                        <PencilSimple size={11} weight="bold" /> Write
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab("preview")}
                        className={`flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest rounded-sm transition-colors ${tab === "preview" ? "bg-slate-800 text-cyan-400" : "text-slate-500 hover:text-cyan-400"}`}
                        data-testid="md-tab-preview"
                    >
                        <Eye size={11} weight="bold" /> Preview
                    </button>
                </div>
            </div>
            {tab === "write" ? (
                <div className="relative">
                    <textarea
                        ref={ref}
                        value={value}
                        onChange={onTextChange}
                        onKeyDown={onKeyDown}
                        onPaste={onPaste}
                        onDrop={onDrop}
                        onDragOver={(e) => e.preventDefault()}
                        placeholder={placeholder}
                        required={required}
                        className={`w-full ${minHeight} bg-[#0b0f19] text-slate-200 text-sm font-mono p-3 focus:outline-none resize-y block`}
                        data-testid="md-textarea"
                    />
                    {mention && mention.results?.length > 0 && (
                        <div
                            className="absolute z-30 left-3 bottom-2 bg-[#0b121f] border border-cyan-500/40 rounded-sm shadow-2xl py-1 min-w-[200px] max-w-[300px]"
                            data-testid="mention-dropdown"
                        >
                            <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-slate-500 border-b border-white/10 font-mono">// @ mention</div>
                            {mention.results.map((u, i) => (
                                <button
                                    type="button"
                                    key={u.user_id}
                                    onMouseDown={(e) => { e.preventDefault(); acceptMention(u); }}
                                    onMouseEnter={() => setMention((m) => ({ ...m, index: i }))}
                                    className={`w-full text-left px-2 py-1.5 flex items-center gap-2 ${i === mention.index ? "bg-cyan-500/10" : "hover:bg-slate-800/50"}`}
                                    data-testid={`mention-option-${u.username}`}
                                >
                                    {u.picture ? (
                                        <img loading="lazy" decoding="async" src={u.picture.startsWith("http") ? u.picture : `/api${u.picture.replace(/^\/api/, "")}`} alt="" className="w-5 h-5 rounded-sm border border-white/10" />
                                    ) : (
                                        <div className="w-5 h-5 rounded-sm bg-slate-800 border border-slate-700 flex items-center justify-center text-[10px] font-bold text-cyan-400">
                                            {u.username[0].toUpperCase()}
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-mono text-cyan-300 truncate">@{u.username}</div>
                                        {u.name && u.name !== u.username && (
                                            <div className="text-[10px] text-slate-500 truncate">{u.name}</div>
                                        )}
                                    </div>
                                    <span className="text-[10px] text-slate-500 font-mono">{u.karma}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div className={`${minHeight} bg-[#0b0f19] p-3 overflow-auto`} data-testid="md-preview">
                    {value?.trim() ? (
                        <Markdown>{value}</Markdown>
                    ) : (
                        <p className="text-xs text-slate-600 font-mono">// nothing to preview yet — write some markdown.</p>
                    )}
                </div>
            )}
        </div>
    );
}
